import React from 'react';
import { Link } from 'react-router-dom';
import PageHelmet from '../../components/seo/PageHelmet';
import Layout from '../../components/ui/Layout';
import { Container, Row, Col, Card, Accordion } from 'react-bootstrap';
import { CONTACT_INFO } from '../../constants/contact';

const FAQ_ITEMS = [
  {
    id: 'payment-methods',
    question: 'What payment methods do you accept?',
    schemaAnswer:
      'Pack My Cart accepts Visa and Mastercard credit and debit cards through WiPay in Jamaican Dollars (JMD). Cash and bank transfers are not accepted online.',
    answer: (
      <>
        <p className="mb-3">
          Pack My Cart accepts online card payments processed securely through{' '}
          <strong>WiPay</strong>, our payment partner in Jamaica. We do not accept cash,
          bank transfers, or in-app payments on this website.
        </p>
        <p className="mb-2 fw-semibold">Accepted payment methods:</p>
        <ul className="mb-3">
          <li>
            <strong>Credit cards</strong> — Visa and Mastercard issued by Jamaican or
            international banks
          </li>
          <li>
            <strong>Debit cards</strong> — Visa Debit and Mastercard Debit cards that
            support online purchases
          </li>
        </ul>
        <p className="mb-2 fw-semibold">Payment details:</p>
        <ul className="mb-0">
          <li>All amounts are charged in <strong>Jamaican Dollars (JMD)</strong></li>
          <li>
            At checkout you may pay a <strong>confirmation fee</strong> to hold your
            order, or the <strong>full order total</strong> when the supermarket offers
            that option
          </li>
          <li>
            You are redirected to WiPay&apos;s secure payment page to enter your card
            details — Pack My Cart never stores your card number
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'payment-security',
    question: 'Is my payment information secure?',
    schemaAnswer:
      'Yes. Card payments are handled on WiPay secure hosted payment page. Pack My Cart never stores your full card number.',
    answer: (
      <p className="mb-0">
        Yes. Card payments are handled on WiPay&apos;s secure hosted payment page, not
        on Pack My Cart. WiPay uses industry-standard encryption for card transactions.
        We only receive confirmation that your payment succeeded — we never see or store
        your full card number.
      </p>
    ),
  },
  {
    id: 'confirmation-fee',
    question: 'What is a confirmation fee?',
    schemaAnswer:
      'The confirmation fee is a mandatory payment on every order that shows your commitment to the purchase. It allows the supermarket to receive your order and begin packing (and delivery, if selected). It helps prevent fake or spam orders on the platform. Any remaining balance is settled with the store at pickup or delivery.',
    answer: (
      <>
        <p className="mb-3">
          The <strong>confirmation fee</strong> is your commitment to the order. When you
          pay it, you are confirming that you are serious about your purchase and that the
          supermarket should start packing your cart — and, if you chose delivery, prepare
          it for delivery.
        </p>
        <p className="mb-3">
          The confirmation fee is <strong>mandatory for all orders</strong>. The
          supermarket will not receive your order for packing until this fee is paid.
          This requirement helps prevent spam and fake orders on the platform, so stores
          only spend time on orders from customers who intend to follow through.
        </p>
        <p className="mb-0">
          If you pay only the confirmation fee at checkout, any remaining balance is
          settled directly with the store when you pick up or receive delivery. Some
          supermarkets also offer full payment upfront — the checkout page shows which
          options are available for your order. See our{' '}
          <Link to="/refund-policy">refund policy</Link> for confirmation fee refund
          terms.
        </p>
      </>
    ),
  },
  {
    id: 'cash-payment',
    question: 'Can I pay with cash?',
    schemaAnswer:
      'Cash is not accepted on the Pack My Cart website. Online checkout requires credit or debit card through WiPay.',
    answer: (
      <p className="mb-0">
        Cash is not accepted on the Pack My Cart website. All online checkout payments
        must be made by credit or debit card through WiPay. If your order has a balance
        remaining after a confirmation fee, the supermarket may accept cash or other
        methods in store — ask the store when you collect your order.
      </p>
    ),
  },
  {
    id: 'place-order',
    question: 'How do I place an order?',
    schemaAnswer:
      'Sign in with Google, choose a supermarket, add items to your cart, and proceed to checkout. Select pickup or delivery and pay online.',
    answer: (
      <p className="mb-0">
        Sign in with Google, choose a supermarket, add items to your cart, and proceed to
        checkout. Select pickup or delivery, review your order, then pay online. See our{' '}
        <Link to="/how-it-works">How It Works</Link> page for a step-by-step guide.
      </p>
    ),
  },
  {
    id: 'track-order',
    question: 'How do I track my order?',
    schemaAnswer:
      'Open Order History or the Order Status page for your transaction to see packing and pickup updates.',
    answer: (
      <p className="mb-0">
        Open <Link to="/order-history">Order History</Link> or the Order Status page for
        your transaction. Status updates include awaiting packing, packed and ready for
        pickup, and completed. You can also enable notifications when you sign in.
      </p>
    ),
  },
  {
    id: 'delivery',
    question: 'Do you offer delivery?',
    schemaAnswer:
      'Delivery is available at participating supermarkets. Choose delivery at checkout and enter your address and phone number.',
    answer: (
      <p className="mb-0">
        Delivery is available at participating supermarkets. At checkout, choose delivery
        and enter your address and phone number. Delivery fees and scheduling are
        arranged with the store — they are not charged on this website during checkout.
      </p>
    ),
  },
  {
    id: 'refunds',
    question: 'Can I get a refund?',
    schemaAnswer:
      'Refund eligibility depends on the situation and our refund policy. Contact packmycart.help@gmail.com with your order details.',
    answer: (
      <p className="mb-0">
        Refund eligibility depends on the situation and our refund policy. Contact us at{' '}
        <a href={`mailto:${CONTACT_INFO.email}`}>{CONTACT_INFO.email}</a> with your order
        details, or read our <Link to="/refund-policy">refund policy</Link> for full
        terms.
      </p>
    ),
  },
  {
    id: 'contact',
    question: 'How do I contact support?',
    schemaAnswer: `Email us at ${CONTACT_INFO.email}. We typically respond within a few business days.`,
    answer: (
      <p className="mb-0">
        Email us at{' '}
        <a href={`mailto:${CONTACT_INFO.email}`}>{CONTACT_INFO.email}</a>. We typically
        respond within a few business days.
      </p>
    ),
  },
];

export default function FAQ() {
  const seoTitle = 'FAQ | Pack My Cart';
  const seoDescription =
    'Frequently asked questions about Pack My Cart — accepted payment methods, ordering, delivery, refunds, and more.';

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.schemaAnswer,
      },
    })),
  };

  return (
    <Layout>
      <PageHelmet
        title={seoTitle}
        description={seoDescription}
        path="/faq"
        jsonLd={faqSchema}
      />

      <style>{`
        .faq-hero {
          background: linear-gradient(135deg, #f8f9fa 0%, #e8f5e9 100%);
          border-radius: 12px;
        }
        .faq-accordion .accordion-button:not(.collapsed) {
          color: var(--text-dark-green, #336555);
          background-color: #f0f7f4;
          box-shadow: none;
        }
        .faq-accordion .accordion-button:focus {
          box-shadow: 0 0 0 0.2rem rgba(51, 101, 85, 0.25);
        }
        .payment-methods-card {
          border-left: 4px solid var(--text-dark-green, #336555);
        }
      `}</style>

      <Container className="mt-4 mb-5">
        <div className="faq-hero text-center px-3 py-4 py-md-5 mb-4 mb-md-5">
          <h1 className="text-dark-green fw-bold mb-3">Frequently Asked Questions</h1>
          <p className="lead mb-0 mx-auto" style={{ maxWidth: '42rem' }}>
            Answers to common questions about shopping, paying, and getting your groceries
            with Pack My Cart.
          </p>
        </div>

        <Row className="justify-content-center mb-4">
          <Col md={10} lg={8}>
            <Card className="payment-methods-card border-0 shadow-sm mb-4">
              <Card.Body className="p-4">
                <h2 className="h5 text-dark-green mb-3">
                  <i className="fas fa-credit-card me-2" aria-hidden />
                  Accepted payment methods
                </h2>
                <p className="mb-2">
                  We accept <strong>Visa</strong> and <strong>Mastercard</strong> credit
                  and debit cards through <strong>WiPay</strong>. All charges are in{' '}
                  <strong>JMD</strong>. Cash and bank transfers are not accepted online.
                </p>
                <p className="mb-0 small text-muted">
                  Expand &ldquo;What payment methods do you accept?&rdquo; below for full
                  details on confirmation fees, full payment, and security.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Accordion defaultActiveKey="payment-methods" className="faq-accordion">
              {FAQ_ITEMS.map((item) => (
                <Accordion.Item eventKey={item.id} key={item.id}>
                  <Accordion.Header>{item.question}</Accordion.Header>
                  <Accordion.Body>{item.answer}</Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>

            <p className="text-center text-muted small mt-4 mb-0">
              New to Pack My Cart? Read{' '}
              <Link to="/how-it-works">How It Works</Link> or email{' '}
              <a href={`mailto:${CONTACT_INFO.email}`}>{CONTACT_INFO.email}</a>.
            </p>
          </Col>
        </Row>
      </Container>
    </Layout>
  );
}
